import { useNavigate } from "react-router";
import { Pencil } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { AvatarUpload } from "@/components/profile/AvatarUpload";
import { CroStatusBadge } from "@/components/profile/CroStatusBadge";
import { ROLE_LABEL } from "@/components/layout/OrganizationSwitcherPartes";
import { IconeColorido } from "@/components/comecar/selecao/IconeColorido";
import { iconeDaCategoria } from "@/components/comecar/selecao/icones-da-selecao";
import { NumerosDoPerfil } from "./NumerosDoPerfil";
import { useRecarregarPerfil } from "./use-recarregar-perfil";

/** Topo do Meu perfil: quem é a pessoa (foto, nome, CRO, especialidades) e os números dela. */
export function CabecalhoDoPerfil() {
  const navigate = useNavigate();
  const recarregar = useRecarregarPerfil();
  const user = trpc.me.current.useQuery().data;
  const especialidades = trpc.specialty.list.useQuery().data ?? [];
  if (!user) return null;

  const minhas = especialidades.filter((e) => user.specialtyIds.includes(e.id));
  // Com CRO é dentista em qualquer organização; sem, vale o papel na organização ativa.
  const papel = user.croNumber ? "Dentista" : user.activeOrganization ? ROLE_LABEL[user.activeOrganization.role] : null;

  return (
    <div className="space-y-4">
      <section
        data-tour="perfil-avatar"
        className="relative flex flex-col items-center gap-6 rounded-2xl border border-brand-100 bg-brand-50 p-5 text-center sm:flex-row sm:items-center sm:p-6 sm:text-left dark:border-primary/20 dark:bg-primary/10"
      >
        <AvatarUpload src={user.image} name={user.name} onChange={recarregar} quadrado />
        <div className="min-w-0 flex-1 space-y-2.5">
          {papel && (
            <span className="inline-flex rounded-md bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-700 dark:bg-primary/20 dark:text-primary">
              {papel}
            </span>
          )}
          <h2 className="truncate text-2xl font-bold text-zinc-900 sm:text-3xl dark:text-foreground">{user.name}</h2>
          {user.croNumber && (
            <p className="flex flex-wrap items-center justify-center gap-2 text-sm text-zinc-600 sm:justify-start dark:text-muted-foreground">
              CRO {user.croNumber}
              {user.croState ? ` - ${user.croState}` : ""}
              <CroStatusBadge status={user.croStatus} />
            </p>
          )}
          {minhas.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 sm:justify-start">
              {minhas.map((e) => (
                <span key={e.id} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white py-0.5 pl-0.5 pr-2 text-xs font-medium text-zinc-700 dark:border-border dark:bg-card dark:text-foreground">
                  <IconeColorido {...iconeDaCategoria(e.label)} tamanho="sm" />
                  {e.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate("/conta/perfil")}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 self-center rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-brand-700 shadow-sm hover:bg-zinc-50 sm:self-start dark:border-border dark:bg-card dark:text-primary"
        >
          <Pencil className="h-4 w-4" />
          Editar perfil
        </button>
      </section>

      <NumerosDoPerfil />
    </div>
  );
}
